import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Heart, Package, ShieldCheck, Download, Trash2, User, MapPin, KeyRound } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { getMyOrders, exportMyData, deleteMyAccount, updateProfile, updateAddress, changePassword } from "@/api";
import { CustomerAuthForms } from "@/components/CustomerAuthForms";
import Seo from "@/components/Seo";

const fmtUsd = (cents) => `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return iso; } };

const EMPTY_ADDR = { full_name: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "", phone: "", courier_note: "" };
const INPUT_CLS = "mt-2 w-full bg-transparent border-b border-line py-2.5 font-light focus:outline-none focus:border-ink transition-colors";

function TextInput({ label, value, onChange, type = "text", placeholder, testid, required }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{label}{required ? " *" : ""}</label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} className={INPUT_CLS} placeholder={placeholder} />
    </div>
  );
}

function SaveBtn({ onClick, saving, testid, children = "Save" }) {
  return (
    <button onClick={onClick} disabled={saving} data-testid={testid}
      className="mt-6 flex items-center gap-2 bg-ink text-paper px-6 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-500 hover:bg-accent hover:text-ink disabled:opacity-60">
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </button>
  );
}

function ContactSection() {
  const { customer, setCustomer, t } = useApp();
  const a = t.account;
  const [name, setName] = useState(customer.name || "");
  const [phone, setPhone] = useState(customer.phone || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { setCustomer(await updateProfile({ name, phone })); toast.success(a.contactSaved); }
    catch { toast.error(a.contactError); }
    finally { setSaving(false); }
  };
  return (
    <section className="mt-12 pt-8 border-t border-line" data-testid="section-contact">
      <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted mb-5"><User className="h-4 w-4" /> {a.contactTitle}</h2>
      <div className="grid sm:grid-cols-2 gap-5">
        <TextInput label={a.name} value={name} onChange={(e) => setName(e.target.value)} testid="contact-name" placeholder={a.namePh} />
        <TextInput label={a.phone} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} testid="contact-phone" placeholder={a.phonePh} />
        <div>
          <label className="text-[10px] uppercase tracking-[0.22em] text-muted">{a.email}</label>
          <input value={customer.email} disabled data-testid="contact-email-ro" className={`${INPUT_CLS} opacity-60 cursor-not-allowed`} />
          <p className="text-[10px] text-muted mt-1">{a.emailLocked}</p>
        </div>
      </div>
      <SaveBtn onClick={save} saving={saving} testid="save-contact-btn">{a.save}</SaveBtn>
    </section>
  );
}

function AddressSection() {
  const { customer, setCustomer, t } = useApp();
  const a = t.account;
  const f = t.payment.fields;
  const [addr, setAddr] = useState({ ...EMPTY_ADDR, ...(customer.default_address || {}) });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setAddr((x) => ({ ...x, [k]: e.target.value }));
  const required = ["full_name", "line1", "city", "postal_code", "country"];
  const save = async () => {
    if (!required.every((k) => (addr[k] || "").trim())) { toast.error(a.addrIncomplete); return; }
    setSaving(true);
    try { setCustomer(await updateAddress(addr)); toast.success(a.addrSaved); }
    catch { toast.error(a.addrError); }
    finally { setSaving(false); }
  };
  return (
    <section className="mt-12 pt-8 border-t border-line" data-testid="section-address">
      <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted mb-2"><MapPin className="h-4 w-4" /> {a.shippingTitle}</h2>
      <p className="text-sm text-ink/50 font-light mb-5">{a.shippingHint}</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2"><TextInput label={f.full_name} value={addr.full_name} onChange={upd("full_name")} testid="addr-full_name" required placeholder={f.full_namePh} /></div>
        <div className="sm:col-span-2"><TextInput label={f.line1} value={addr.line1} onChange={upd("line1")} testid="addr-line1" required placeholder={f.line1Ph} /></div>
        <div className="sm:col-span-2"><TextInput label={f.line2} value={addr.line2} onChange={upd("line2")} testid="addr-line2" placeholder={f.line2Ph} /></div>
        <TextInput label={f.city} value={addr.city} onChange={upd("city")} testid="addr-city" required placeholder={f.cityPh} />
        <TextInput label={f.state} value={addr.state} onChange={upd("state")} testid="addr-state" placeholder={f.statePh} />
        <TextInput label={f.postal_code} value={addr.postal_code} onChange={upd("postal_code")} testid="addr-postal_code" required placeholder={f.postal_codePh} />
        <TextInput label={f.country} value={addr.country} onChange={upd("country")} testid="addr-country" required placeholder={f.countryPh} />
        <div className="sm:col-span-2"><TextInput label={f.phone} type="tel" value={addr.phone} onChange={upd("phone")} testid="addr-phone" placeholder={f.phonePh} /></div>
      </div>
      <SaveBtn onClick={save} saving={saving} testid="save-address-btn">{a.save}</SaveBtn>
    </section>
  );
}

function PasswordSection() {
  const { customer, t } = useApp();
  const a = t.account;
  const hasPassword = customer.has_password;
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [conf, setConf] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (next.length < 6) { toast.error(a.pwShort); return; }
    if (next !== conf) { toast.error(a.pwMismatch); return; }
    setSaving(true);
    try {
      await changePassword({ current_password: hasPassword ? cur : undefined, new_password: next });
      toast.success(hasPassword ? a.pwChanged : a.pwSet);
      setCur(""); setNext(""); setConf("");
    } catch (err) {
      toast.error(err.response?.data?.detail || a.pwError);
    } finally { setSaving(false); }
  };
  return (
    <section className="mt-12 pt-8 border-t border-line" data-testid="section-password">
      <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted mb-2"><KeyRound className="h-4 w-4" /> {hasPassword ? a.passwordTitle : a.setPasswordTitle}</h2>
      {!hasPassword && <p className="text-sm text-ink/50 font-light mb-5">{a.googleHint}</p>}
      <div className="grid sm:grid-cols-2 gap-5">
        {hasPassword && (
          <div className="sm:col-span-2 sm:max-w-xs"><TextInput label={a.currentPassword} type="password" value={cur} onChange={(e) => setCur(e.target.value)} testid="pw-current" placeholder="••••••••" /></div>
        )}
        <TextInput label={a.newPassword} type="password" value={next} onChange={(e) => setNext(e.target.value)} testid="pw-new" placeholder={a.newPasswordPh} />
        <TextInput label={a.confirmPassword} type="password" value={conf} onChange={(e) => setConf(e.target.value)} testid="pw-confirm" placeholder={a.confirmPasswordPh} />
      </div>
      <SaveBtn onClick={save} saving={saving} testid="save-password-btn">{hasPassword ? a.changeBtn : a.setBtn}</SaveBtn>
    </section>
  );
}


function Profile() {
  const { customer, logoutCustomer, setWishlistOpen, t } = useApp();
  const a = t.account;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { getMyOrders().then(setOrders).catch(() => {}).finally(() => setLoading(false)); }, []);

  const handleExport = async () => {
    setBusy(true);
    try {
      const data = await exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "my-data-heda-gelogaeva.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success(a.dataDownloaded);
    } catch {
      toast.error(a.dataError);
    } finally { setBusy(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(a.deleteConfirm)) return;
    setBusy(true);
    try {
      await deleteMyAccount();
      toast.success(a.deleted);
      logoutCustomer();
    } catch {
      toast.error(a.deleteError);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="account-profile">
      <div className="flex items-center justify-between mb-10">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted mb-1">{a.signedInAs}</p>
          <p className="font-display text-2xl">{customer.name || customer.email}</p>
          <p className="text-sm text-ink/50">{customer.email}</p>
        </div>
        <button onClick={logoutCustomer} data-testid="logout-btn"
          className="flex items-center gap-2 border border-line px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:border-ink transition-colors">
          <LogOut className="h-4 w-4" /> {a.signOut}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-12">
        <button onClick={() => setWishlistOpen(true)} data-testid="account-wishlist-btn"
          className="flex items-center gap-2 border border-line px-4 py-2.5 text-xs uppercase tracking-[0.16em] hover:border-ink transition-colors">
          <Heart className="h-4 w-4" /> {a.favourites}
        </button>
        <Link to="/catalogue" className="flex items-center gap-2 border border-line px-4 py-2.5 text-xs uppercase tracking-[0.16em] hover:border-ink transition-colors">
          {a.continueShopping}
        </Link>
      </div>

      <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted mb-4"><Package className="h-4 w-4" /> {a.orderHistory}</h2>
      {loading ? (
        <Loader2 className="h-6 w-6 animate-spin text-accent" />
      ) : orders.length === 0 ? (
        <p className="text-ink/50 border-y border-line py-10 text-center" data-testid="orders-empty">{a.noOrders}</p>
      ) : (
        <div className="space-y-3" data-testid="my-orders">
          {orders.map((o) => (
            <div key={o.session_id} className="border border-line p-5" data-testid={`my-order-${o.session_id}`}>
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-line">
                <span className="text-[10px] uppercase tracking-[0.2em] text-muted">
                  {o.order_number ? <span className="text-ink font-medium">#{o.order_number} · </span> : null}
                  {(o.provider || "stripe") === "paypal" ? "PayPal" : "Card"} · {fmtDate(o.created_at)}
                </span>
                <span className="font-display text-xl">{fmtUsd(o.amount)}</span>
              </div>
              {(o.items || []).map((it, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-ink/70"><span className="text-muted">{it.brand}</span> {it.name}{it.size ? ` · ${it.size}` : ""}</span>
                  <span className="text-ink/60">×{it.quantity || 1}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <ContactSection />
      <AddressSection />
      <PasswordSection />

      <div className="mt-14 pt-8 border-t border-line" data-testid="privacy-data">
        <h2 className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-muted mb-3">
          <ShieldCheck className="h-4 w-4" /> {a.privacyTitle}
        </h2>
        <p className="text-sm text-ink/50 font-light max-w-lg mb-5">
          {a.privacyText}{" "}
          <Link to="/legal/privacy" className="link-underline">{a.privacyPolicy}</Link>.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={handleExport} disabled={busy} data-testid="export-data-btn"
            className="flex items-center gap-2 border border-line px-4 py-2.5 text-xs uppercase tracking-[0.16em] hover:border-ink transition-colors disabled:opacity-60">
            <Download className="h-4 w-4" /> {a.download}
          </button>
          <button onClick={handleDelete} disabled={busy} data-testid="delete-account-btn"
            className="flex items-center gap-2 border border-red-300 text-red-700 px-4 py-2.5 text-xs uppercase tracking-[0.16em] hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors disabled:opacity-60">
            <Trash2 className="h-4 w-4" /> {a.delete}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Account() {
  const { customer, t } = useApp();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="pt-32 md:pt-40 pb-24 px-5 md:px-10 min-h-[70vh]" data-testid="account-page">
      <Seo title={t.seo.account.title} description={t.seo.account.description} noindex />
      <div className="max-w-6xl mx-auto">
        {customer ? (
          <>
            <h1 className="font-display font-light text-5xl md:text-6xl mb-10">{t.account.headingIn}</h1>
            <Profile />
          </>
        ) : (
          <div className="grid md:grid-cols-2 gap-12 lg:gap-24 md:items-center">
            <div>
              <h1 className="font-display font-light text-5xl md:text-6xl mb-6">{t.account.heading}</h1>
              <p className="text-ink/60 font-light leading-relaxed max-w-sm">
                {t.account.tagline}
              </p>
            </div>
            <div className="flex md:justify-end">
              <div className="w-full max-w-md">
                <CustomerAuthForms redirectPath="/account" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
